import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Switch,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Autocomplete,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  MedicalServices,
  Groups,
  CheckCircleOutline,
  ErrorOutline,
  KeyboardReturn,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/axios';
import { PhoneInput } from '../components/PhoneInput';

/* ────────────────────────────────────────────────────────────
   Design tokens — "Clinical Editorial"
   ──────────────────────────────────────────────────────────── */
const NAVY = '#0D1B35';
const NAVY_INK = '#15203C';
const NAVY_MUTE = 'rgba(13,27,53,0.60)';
const NAVY_FAINT = 'rgba(13,27,53,0.38)';
const HAIRLINE = 'rgba(13,27,53,0.10)';
const HAIRLINE_SOFT = 'rgba(13,27,53,0.06)';
const BLUE = '#0066CC';
const BLUE_WASH = 'rgba(0,102,204,0.08)';
const AMBER = '#B98A3A';

// Bricolage Grotesque is now the global heading font (loaded in apps/web/index.html and
// wired into the MUI theme's h1..h6 variants). DISPLAY stays defined here for
// the few places that style raw Typography with explicit sx instead of using
// a heading variant.
const DISPLAY = '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif';
// Body matches the rest of the app — Plus Jakarta Sans (no more Instrument Sans).
const SANS = '"Plus Jakarta Sans", system-ui, sans-serif';
const MONO = '"SF Mono", "Menlo", "Courier New", monospace';

/* ────────────────────────────────────────────────────────────
   Types
   ──────────────────────────────────────────────────────────── */
interface DoctorProfile {
  id: string;
  firstName: string;
  lastName: string;
  licenseNumber: string;
  phoneNumber: string;
  email?: string;
  specialtyId?: string;
  specialtyName?: string;
  clinicName?: string;
  rncTaxId?: string;
  clinicAddress?: string;
  cmdNumber?: string;
  isVerified: boolean;
  canPrescribe: boolean;
  canViewPatients: boolean;
}

interface UserDetail {
  id: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  doctorProfile?: DoctorProfile;
}

interface Specialty {
  id: string;
  name: string;
}

interface FormState {
  firstName: string;
  lastName: string;
  licenseNumber: string;
  phoneNumber: string;
  email: string;
  specialtyId: string;
  clinicName: string;
  rncTaxId: string;
  clinicAddress: string;
  cmdNumber: string;
  isVerified: boolean;
  canPrescribe: boolean;
  canViewPatients: boolean;
}

const PROFILE_KEYS: (keyof FormState)[] = [
  'firstName', 'lastName', 'licenseNumber', 'phoneNumber', 'email',
  'specialtyId', 'clinicName', 'rncTaxId', 'clinicAddress', 'cmdNumber',
  'isVerified',
];
const FEATURE_KEYS: (keyof FormState)[] = ['canPrescribe', 'canViewPatients'];

const emptyForm: FormState = {
  firstName: '', lastName: '', licenseNumber: '', phoneNumber: '', email: '',
  specialtyId: '', clinicName: '', rncTaxId: '', clinicAddress: '',
  cmdNumber: '', isVerified: false, canPrescribe: false, canViewPatients: false,
};

/* ────────────────────────────────────────────────────────────
   Component
   ──────────────────────────────────────────────────────────── */
export const EditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [initial, setInitial] = useState<FormState>(emptyForm);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const seededRef = useRef(false);

  const { data: user, isLoading, error } = useQuery<UserDetail>({
    queryKey: ['admin-user', id],
    queryFn: async () => {
      const { data } = await apiClient.get<UserDetail>(`/admin/users/${id}`);
      return data;
    },
    enabled: !!id,
  });

  const { data: specialties } = useQuery<Specialty[]>({
    queryKey: ['specialties'],
    queryFn: async () => {
      const { data } = await apiClient.get<Specialty[]>('/specialties');
      return data;
    },
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!seededRef.current && user?.doctorProfile) {
      const d = user.doctorProfile;
      const seed: FormState = {
        firstName: d.firstName ?? '',
        lastName: d.lastName ?? '',
        licenseNumber: d.licenseNumber ?? '',
        phoneNumber: d.phoneNumber ?? '',
        email: d.email ?? '',
        specialtyId: d.specialtyId ?? '',
        clinicName: d.clinicName ?? '',
        rncTaxId: d.rncTaxId ?? '',
        clinicAddress: d.clinicAddress ?? '',
        cmdNumber: d.cmdNumber ?? '',
        isVerified: d.isVerified,
        canPrescribe: d.canPrescribe,
        canViewPatients: d.canViewPatients,
      };
      setForm(seed);
      setInitial(seed);
      seededRef.current = true;
    }
  }, [user]);

  const changedKeys = useMemo(() => {
    if (!seededRef.current) return [] as (keyof FormState)[];
    return (Object.keys(form) as (keyof FormState)[]).filter((k) => form[k] !== initial[k]);
  }, [form, initial]);

  const isDirty = changedKeys.length > 0;
  const isFieldChanged = (k: keyof FormState) => changedKeys.includes(k);

  const { mutate: saveAll, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      const profileChanged = PROFILE_KEYS.some((k) => form[k] !== initial[k]);
      const featuresChanged = FEATURE_KEYS.some((k) => form[k] !== initial[k]);

      const requests: Promise<any>[] = [];
      if (profileChanged) {
        requests.push(
          apiClient.put(`/admin/users/${id}/doctor-profile`, {
            firstName: form.firstName || undefined,
            lastName: form.lastName || undefined,
            licenseNumber: form.licenseNumber || undefined,
            phoneNumber: form.phoneNumber || undefined,
            email: form.email || undefined,
            specialtyId: form.specialtyId || undefined,
            clinicName: form.clinicName,
            rncTaxId: form.rncTaxId,
            clinicAddress: form.clinicAddress,
            cmdNumber: form.cmdNumber,
            isVerified: form.isVerified,
          }),
        );
      }
      if (featuresChanged) {
        requests.push(
          apiClient.put(`/admin/users/${id}/doctor-features`, {
            canPrescribe: form.canPrescribe,
            canViewPatients: form.canViewPatients,
          }),
        );
      }

      const results = await Promise.allSettled(requests);
      const rejected = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
      if (rejected) throw rejected.reason;
    },
    onSuccess: () => {
      setSavedAt(new Date());
      setSaveError(null);
      setInitial(form); // new baseline
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user', id] });
    },
    onError: (err: any) => {
      setSaveError(err?.response?.data?.message ?? 'Error al guardar. Intenta de nuevo.');
    },
  });

  const handleChange = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setSavedAt(null);
    setSaveError(null);
  };

  const handleDiscard = () => {
    setForm(initial);
    setSavedAt(null);
    setSaveError(null);
  };

  // Keyboard shortcut: Cmd/Ctrl + S
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (isDirty && !isSaving) saveAll();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isDirty, isSaving, saveAll]);

  // Clear the "saved" confirmation after a few seconds
  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(null), 3000);
    return () => clearTimeout(t);
  }, [savedAt]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Alert severity="error" sx={{ mt: 3 }}>
        Usuario no encontrado.
      </Alert>
    );
  }

  // A doctor who hasn't completed onboarding has a doctor row with null
  // firstName/lastName. There's nothing meaningful to edit yet — the doctor
  // will fill out their own profile via the invitation link.
  const isPendingDoctor =
    user.role === 'DOCTOR' &&
    (!user.doctorProfile?.firstName || !user.doctorProfile?.lastName);

  if (isPendingDoctor) {
    return (
      <Box sx={{ fontFamily: SANS }}>
        <Box
          sx={{
            borderRadius: 4,
            bgcolor: NAVY,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
            p: { xs: '24px 20px', sm: '32px 40px' },
            mb: 3,
            color: '#fff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton size="small" onClick={() => navigate('/admin/users')} sx={{ color: 'rgba(255,255,255,0.55)', p: 0.5 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', fontFamily: SANS }}>
              Usuarios
            </Typography>
          </Box>
          <Typography variant="h2">
            {user.email}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 4, sm: 6 }, textAlign: 'center' }}
        >
          <Typography variant="h3" sx={{ color: NAVY, mb: 1 }}>
            Perfil aún no disponible
          </Typography>
          <Typography sx={{ color: NAVY_MUTE, fontFamily: SANS, fontSize: 14, maxWidth: 480, mx: 'auto' }}>
            Este doctor todavía no ha completado su onboarding. Podrás editar su perfil
            una vez que acepte la invitación y complete sus datos. Mientras tanto, puedes
            cancelar la invitación eliminando el usuario desde la lista.
          </Typography>
          <Button
            onClick={() => navigate('/admin/users')}
            sx={{ mt: 3, fontFamily: SANS, textTransform: 'none', color: BLUE, fontWeight: 600 }}
          >
            Volver a usuarios
          </Button>
        </Paper>
      </Box>
    );
  }

  const isDoctor = user.role === 'DOCTOR' && !!user.doctorProfile;
  const displayName = isDoctor
    ? `Dr. ${user.doctorProfile!.firstName} ${user.doctorProfile!.lastName}`
    : user.email;
  const initials = isDoctor
    ? `${(user.doctorProfile!.firstName || '?')[0]}${(user.doctorProfile!.lastName || '')[0] || ''}`.toUpperCase()
    : user.email[0]?.toUpperCase() ?? '?';

  return (
    <Box sx={{ pb: isDoctor ? { xs: 18, sm: 14 } : 4, fontFamily: SANS }}>
      {/* ───────── Hero ───────── */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage:
            'radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), radial-gradient(circle at 80% -10%, rgba(0,102,204,0.25), transparent 55%)',
          backgroundSize: '22px 22px, 100% 100%',
          p: { xs: '28px 24px', sm: '44px 48px' },
          mb: { xs: 3, sm: 4 },
          position: 'relative',
          overflow: 'hidden',
          animation: 'eu-rise .55s cubic-bezier(.2,.8,.2,1) both',
          '@keyframes eu-rise': {
            from: { opacity: 0, transform: 'translateY(8px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.10)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.06)', bottom: -50, left: '38%', pointerEvents: 'none' }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 2.5 } }}>
          <IconButton size="small" onClick={() => navigate('/admin/users')} sx={{ color: 'rgba(255,255,255,0.55)', p: 0.5 }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.55)',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontFamily: SANS,
            }}
          >
            Usuarios · Editor
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, sm: 3 } }}>
          {/* Monogram */}
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: 56, sm: 72 },
              height: { xs: 56, sm: 72 },
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                fontFamily: DISPLAY,
                fontWeight: 500,
                fontSize: { xs: 22, sm: 28 },
                letterSpacing: '-0.02em',
              }}
            >
              {initials}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h1" sx={{ color: '#fff', mb: 0.75 }}>
              {displayName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={user.role.replace('_', ' ')}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.10)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 10.5,
                  letterSpacing: '0.08em',
                  fontFamily: SANS,
                  textTransform: 'uppercase',
                  border: '1px solid rgba(255,255,255,0.12)',
                  height: 22,
                }}
              />
              {!user.isActive && (
                <Chip
                  label="Inactivo"
                  size="small"
                  sx={{ bgcolor: 'rgba(239,68,68,0.22)', color: '#fca5a5', fontWeight: 600, fontSize: 11, height: 22, fontFamily: SANS }}
                />
              )}
              {isDoctor && user.doctorProfile?.specialtyName && (
                <Chip
                  label={user.doctorProfile.specialtyName}
                  size="small"
                  sx={{
                    bgcolor: 'transparent',
                    color: 'rgba(255,255,255,0.72)',
                    fontSize: 11,
                    height: 22,
                    fontFamily: SANS,
                    border: '1px dashed rgba(255,255,255,0.22)',
                  }}
                />
              )}
              {isDoctor && user.doctorProfile?.isVerified && (
                <Chip
                  icon={<CheckCircleOutline sx={{ fontSize: '14px !important', color: `${AMBER} !important` }} />}
                  label="Verificado"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(185,138,58,0.14)',
                    color: '#F0D8A3',
                    fontWeight: 600,
                    fontSize: 10.5,
                    height: 22,
                    fontFamily: SANS,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    '& .MuiChip-icon': { ml: 0.5 },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {isDoctor && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 1.5, sm: 3 },
              mt: { xs: 2.5, sm: 3 },
              pt: { xs: 2.5, sm: 3 },
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <MetaItem label="Correo de acceso" value={user.email} />
            <MetaItem label="Licencia" value={user.doctorProfile!.licenseNumber || '—'} mono />
            {user.doctorProfile?.phoneNumber && (
              <MetaItem label="Teléfono" value={user.doctorProfile.phoneNumber} mono />
            )}
          </Box>
        )}
      </Box>

      {/* ───────── Editor Surface ───────── */}
      {isDoctor ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            px: { xs: 2.5, sm: 4, md: 6 },
            py: { xs: 1, sm: 2 },
          }}
        >
          <Section index="01" title="Identidad" delay={0.05}>
            <FieldRow>
              <Field
                label="Nombre"
                value={form.firstName}
                onChange={(v) => handleChange('firstName', v)}
                changed={isFieldChanged('firstName')}
              />
              <Field
                label="Apellido"
                value={form.lastName}
                onChange={(v) => handleChange('lastName', v)}
                changed={isFieldChanged('lastName')}
              />
            </FieldRow>
            <FieldRow>
              <Field
                label="Número de licencia"
                value={form.licenseNumber}
                onChange={(v) => handleChange('licenseNumber', v)}
                changed={isFieldChanged('licenseNumber')}
                mono
              />
              <Field
                label="Número CMD"
                value={form.cmdNumber}
                onChange={(v) => handleChange('cmdNumber', v)}
                changed={isFieldChanged('cmdNumber')}
                mono
              />
            </FieldRow>
            <FieldRow>
              <SpecialtyField
                value={form.specialtyId}
                onChange={(id) => handleChange('specialtyId', id)}
                options={specialties ?? []}
                changed={isFieldChanged('specialtyId')}
              />
            </FieldRow>

            <VerifiedToggle
              checked={form.isVerified}
              onChange={(v) => handleChange('isVerified', v)}
              changed={isFieldChanged('isVerified')}
            />
          </Section>

          <SectionRule />

          <Section index="02" title="Contacto" delay={0.12}>
            <FieldRow>
              <PhoneField
                value={form.phoneNumber}
                onChange={(v) => handleChange('phoneNumber', v)}
                changed={isFieldChanged('phoneNumber')}
              />
              <Field
                label="Correo de perfil"
                value={form.email}
                onChange={(v) => handleChange('email', v)}
                changed={isFieldChanged('email')}
                hint="Opcional · distinto del correo de inicio de sesión"
              />
            </FieldRow>
          </Section>

          <SectionRule />

          <Section
            index="03"
            title="Consultorio"
            subtitle="Aparece en el encabezado de las recetas impresas"
            delay={0.19}
          >
            <FieldRow>
              <Field
                label="Nombre de la clínica"
                value={form.clinicName}
                onChange={(v) => handleChange('clinicName', v)}
                changed={isFieldChanged('clinicName')}
              />
              <Field
                label="RNC (Tax ID)"
                value={form.rncTaxId}
                onChange={(v) => handleChange('rncTaxId', v)}
                changed={isFieldChanged('rncTaxId')}
                mono
              />
            </FieldRow>
            <FieldRow>
              <Field
                label="Dirección de la clínica"
                value={form.clinicAddress}
                onChange={(v) => handleChange('clinicAddress', v)}
                changed={isFieldChanged('clinicAddress')}
                span={2}
              />
            </FieldRow>
          </Section>

          <SectionRule />

          <Section
            index="04"
            title="Módulos"
            subtitle="Qué puede hacer este médico dentro de la plataforma"
            delay={0.26}
          >
            <ModuleRow
              icon={<MedicalServices sx={{ fontSize: 20 }} />}
              title="Recetas electrónicas"
              description="Crear, firmar e imprimir prescripciones"
              checked={form.canPrescribe}
              onChange={(v) => handleChange('canPrescribe', v)}
              changed={isFieldChanged('canPrescribe')}
            />
            <ModuleRow
              icon={<Groups sx={{ fontSize: 20 }} />}
              title="Pacientes"
              description="Administrar historial y lista de pacientes"
              checked={form.canViewPatients}
              onChange={(v) => handleChange('canViewPatients', v)}
              changed={isFieldChanged('canViewPatients')}
              last
            />
          </Section>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" sx={{ color: NAVY, mb: 1 }}>
            Sin configuraciones adicionales
          </Typography>
          <Typography sx={{ color: NAVY_MUTE, fontFamily: SANS, fontSize: 14 }}>
            Este tipo de usuario no tiene un perfil extendido para editar desde este panel.
          </Typography>
        </Paper>
      )}

      {/* ───────── Sticky Commit Bar ───────── */}
      {isDoctor && (
        <CommitBar
          visible={isDirty || isSaving || !!saveError || !!savedAt}
          changedCount={changedKeys.length}
          isSaving={isSaving}
          savedAt={savedAt}
          error={saveError}
          onSave={() => saveAll()}
          onDiscard={handleDiscard}
        />
      )}
    </Box>
  );
};

/* ────────────────────────────────────────────────────────────
   Sub-components
   ──────────────────────────────────────────────────────────── */

const MetaItem: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <Box>
    <Typography
      sx={{
        color: 'rgba(255,255,255,0.42)',
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        fontFamily: SANS,
        mb: 0.375,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: 'rgba(255,255,255,0.88)',
        fontSize: 13.5,
        fontWeight: 500,
        fontFamily: mono ? MONO : SANS,
      }}
    >
      {value}
    </Typography>
  </Box>
);

interface SectionProps {
  index: string;
  title: string;
  subtitle?: string;
  delay?: number;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ index, title, subtitle, delay = 0, children }) => (
  <Box
    sx={{
      py: { xs: 3.5, sm: 5 },
      animation: 'eu-sec .6s cubic-bezier(.2,.8,.2,1) both',
      animationDelay: `${delay}s`,
      '@keyframes eu-sec': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2 }}>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 10.5,
          fontWeight: 500,
          color: BLUE,
          letterSpacing: '0.02em',
        }}
      >
        {index}
      </Typography>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h2" sx={{ color: NAVY, lineHeight: 1 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{ fontFamily: SANS, fontSize: 13, color: NAVY_MUTE, mt: 0.75, maxWidth: 520 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ mt: { xs: 3, sm: 3.5 } }}>{children}</Box>
  </Box>
);

const SectionRule: React.FC = () => (
  <Box sx={{ height: 1, bgcolor: HAIRLINE_SOFT, mx: { xs: 0, sm: -2 } }} />
);

const FieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: { xs: 3, sm: 4 },
      mb: { xs: 3, sm: 3.5 },
      '&:last-child': { mb: 0 },
    }}
  >
    {children}
  </Box>
);

/* Shared styling for outlined-box fields (TextField + Autocomplete + PhoneField) */
const labelTypographySx = (changed?: boolean) => ({
  fontFamily: SANS,
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: changed ? BLUE : NAVY_MUTE,
  bgcolor: 'background.paper',
  px: 0.75,
  '&.Mui-focused': { color: BLUE },
});

/* Used with MUI outlined TextField / Autocomplete — needs transform override
   since MUI's shrunk label applies scale(0.75) by default. */
const labelSx = (changed?: boolean) => ({
  ...labelTypographySx(changed),
  transform: 'translate(12px, -6px) scale(1)',
  '&.MuiInputLabel-shrink': { transform: 'translate(12px, -6px) scale(1)' },
});

const inputSx = (changed?: boolean, mono?: boolean) => ({
  fontFamily: mono ? MONO : SANS,
  fontSize: mono ? 14 : 15,
  color: NAVY_INK,
  borderRadius: '6px',
  bgcolor: 'transparent',
  '& input': { py: 1.5, px: 1.75 },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: changed ? BLUE : HAIRLINE,
    transition: 'border-color .2s',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: changed ? BLUE : NAVY_FAINT,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: BLUE,
    borderWidth: 2,
  },
});

const helperSx = {
  fontFamily: SANS,
  fontSize: 11.5,
  color: NAVY_FAINT,
  fontStyle: 'italic' as const,
  ml: 0.5,
  mt: 0.75,
};

const ChangedDot: React.FC = () => (
  <Box
    sx={{
      position: 'absolute',
      top: -3,
      right: 14,
      width: 5,
      height: 5,
      borderRadius: '50%',
      bgcolor: BLUE,
      animation: 'eu-pulse 1.6s ease-in-out infinite',
      '@keyframes eu-pulse': {
        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.5, transform: 'scale(0.85)' },
      },
      zIndex: 2,
      pointerEvents: 'none',
    }}
  />
);

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  changed?: boolean;
  hint?: string;
  mono?: boolean;
  span?: 1 | 2;
}

const Field: React.FC<FieldProps> = ({ label, value, onChange, changed, hint, mono, span = 1 }) => (
  <Box sx={{ gridColumn: span === 2 ? { xs: '1', sm: '1 / -1' } : undefined, position: 'relative' }}>
    <TextField
      variant="outlined"
      label={label}
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText={hint}
      InputLabelProps={{ shrink: true, sx: labelSx(changed) }}
      InputProps={{ sx: inputSx(changed, mono) }}
      FormHelperTextProps={{ sx: helperSx }}
    />
    {changed && <ChangedDot />}
  </Box>
);

/* Searchable Specialty picker — Autocomplete styled like the other outlined fields */
interface SpecialtyFieldProps {
  value: string;
  onChange: (id: string) => void;
  options: Specialty[];
  changed?: boolean;
}

const SpecialtyField: React.FC<SpecialtyFieldProps> = ({ value, onChange, options, changed }) => {
  const selected = options.find((s) => s.id === value) ?? null;
  return (
    <Box sx={{ position: 'relative' }}>
      <Autocomplete<Specialty>
        options={options}
        value={selected}
        onChange={(_, next) => onChange(next?.id ?? '')}
        getOptionLabel={(opt) => opt.name}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        noOptionsText="Sin resultados"
        clearOnEscape
        autoHighlight
        openOnFocus
        componentsProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 2,
              border: `1px solid ${HAIRLINE}`,
              boxShadow: '0 12px 32px -12px rgba(13,27,53,0.22)',
              fontFamily: SANS,
            },
          },
        }}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.id}
            style={{
              fontFamily: SANS,
              fontSize: 14,
              color: NAVY_INK,
              padding: '8px 14px',
            }}
          >
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Especialidad"
            placeholder="Buscar especialidad…"
            InputLabelProps={{ ...params.InputLabelProps, shrink: true, sx: labelSx(changed) }}
            InputProps={{
              ...params.InputProps,
              sx: {
                ...inputSx(changed),
                // Autocomplete's wrapper injects its own !important padding —
                // override it so the box matches the height of plain Fields.
                py: '0 !important',
                '& .MuiAutocomplete-input': {
                  py: '12px !important',
                  px: '14px !important',
                },
              },
            }}
          />
        )}
      />
      {changed && <ChangedDot />}
    </Box>
  );
};

const PhoneField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  changed?: boolean;
}> = ({ value, onChange, changed }) => (
  <Box
    sx={{
      position: 'relative',
      '& .MuiFormHelperText-root': { display: 'none' },
      '& .PhoneInput': {
        padding: '11.5px 14px !important',
        borderRadius: '6px !important',
        borderColor: `${changed ? BLUE : HAIRLINE} !important`,
      },
      '& .PhoneInput:focus-within': {
        borderColor: `${BLUE} !important`,
        borderWidth: '2px !important',
        padding: '10.5px 13px !important',
      },
      '& .PhoneInputInput': {
        fontFamily: `${MONO} !important`,
        fontSize: '14px !important',
        color: NAVY_INK,
      },
      '& .MuiInputLabel-root': labelTypographySx(changed),
    }}
  >
    <PhoneInput label="Teléfono" value={value} onChange={onChange} />
    {changed && <ChangedDot />}
  </Box>
);

const VerifiedToggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  changed?: boolean;
}> = ({ checked, onChange, changed }) => (
  <Box
    onClick={() => onChange(!checked)}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChange(!checked);
      }
    }}
    sx={{
      mt: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      px: 2,
      py: 1.5,
      borderRadius: 2,
      cursor: 'pointer',
      border: '1px solid',
      borderColor: checked ? alpha(AMBER, 0.35) : HAIRLINE,
      bgcolor: checked ? alpha(AMBER, 0.05) : 'transparent',
      transition: 'all .25s ease',
      '&:hover': { borderColor: checked ? alpha(AMBER, 0.55) : NAVY_FAINT },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: checked ? AMBER : 'transparent',
          border: `1px solid ${checked ? AMBER : HAIRLINE}`,
          color: checked ? '#fff' : NAVY_FAINT,
          transition: 'all .25s ease',
        }}
      >
        <CheckCircleOutline sx={{ fontSize: 16 }} />
      </Box>
      <Box>
        <Typography sx={{ fontFamily: SANS, fontWeight: 600, fontSize: 13.5, color: NAVY }}>
          Identidad verificada
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: 11.5, color: NAVY_MUTE }}>
          Marcar cuando se haya validado la licencia manualmente
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {changed && (
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: BLUE }} />
      )}
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        size="small"
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: AMBER },
        }}
      />
    </Box>
  </Box>
);

interface ModuleRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  changed?: boolean;
  last?: boolean;
}

const ModuleRow: React.FC<ModuleRowProps> = ({ icon, title, description, checked, onChange, changed, last }) => (
  <Box
    onClick={() => onChange(!checked)}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChange(!checked);
      }
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2.5,
      py: { xs: 2, sm: 2.25 },
      cursor: 'pointer',
      borderBottom: last ? 'none' : `1px solid ${HAIRLINE_SOFT}`,
      transition: 'background-color .2s ease',
      '&:hover': { bgcolor: alpha(BLUE, 0.02) },
      '&:focus-visible': { outline: `2px solid ${alpha(BLUE, 0.35)}`, outlineOffset: 2, borderRadius: 8 },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '10px',
        display: 'grid',
        placeItems: 'center',
        bgcolor: checked ? BLUE_WASH : 'rgba(13,27,53,0.035)',
        color: checked ? BLUE : NAVY_FAINT,
        transition: 'all .3s ease',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="h6" sx={{ color: NAVY }}>
          {title}
        </Typography>
        {changed && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: BLUE }} />}
      </Box>
      <Typography sx={{ fontFamily: SANS, fontSize: 12.5, color: NAVY_MUTE, mt: 0.25 }}>
        {description}
      </Typography>
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: BLUE },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: BLUE },
      }}
    />
  </Box>
);

/* ────────────────────────────────────────────────────────────
   Commit Bar — sticky, single save action
   ──────────────────────────────────────────────────────────── */
interface CommitBarProps {
  visible: boolean;
  changedCount: number;
  isSaving: boolean;
  savedAt: Date | null;
  error: string | null;
  onSave: () => void;
  onDiscard: () => void;
}

const CommitBar: React.FC<CommitBarProps> = ({
  visible,
  changedCount,
  isSaving,
  savedAt,
  error,
  onSave,
  onDiscard,
}) => {
  const mode: 'dirty' | 'saving' | 'saved' | 'error' = error
    ? 'error'
    : isSaving
    ? 'saving'
    : savedAt
    ? 'saved'
    : 'dirty';

  return (
    <Box
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: { xs: 16, sm: 24 },
        transform: visible
          ? 'translateX(-50%) translateY(0)'
          : 'translateX(-50%) translateY(calc(100% + 60px))',
        transition: 'transform .45s cubic-bezier(.2,.8,.2,1)',
        zIndex: 1200,
        width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
        maxWidth: { xs: 'calc(100vw - 32px)', sm: 560 },
        minWidth: { sm: 440 },
      }}
    >
      <Box
        sx={{
          bgcolor: NAVY,
          borderRadius: 999,
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.25, sm: 1.25 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          boxShadow:
            '0 1px 0 rgba(255,255,255,0.08) inset, 0 24px 60px -14px rgba(13,27,53,0.55), 0 6px 16px -8px rgba(13,27,53,0.4)',
          border: '1px solid rgba(255,255,255,0.06)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Status dot + text */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 }, flex: 1, minWidth: 0 }}>
          <StatusDot mode={mode} />
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: 13,
              fontWeight: 500,
              color: mode === 'error' ? '#FCA5A5' : mode === 'saved' ? '#86EFAC' : 'rgba(255,255,255,0.88)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {mode === 'error' && (error || 'Error al guardar')}
            {mode === 'saving' && 'Guardando…'}
            {mode === 'saved' && 'Cambios guardados'}
            {mode === 'dirty' &&
              (changedCount === 1 ? '1 cambio sin guardar' : `${changedCount} cambios sin guardar`)}
          </Typography>
        </Box>

        {/* Actions */}
        {(mode === 'dirty' || mode === 'error') && (
          <>
            <Button
              onClick={onDiscard}
              disabled={isSaving}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontFamily: SANS,
                fontWeight: 500,
                fontSize: 12.5,
                textTransform: 'none',
                minHeight: 32,
                minWidth: 0,
                px: 1.5,
                display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              Descartar
            </Button>
            <Tooltip
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: SANS, fontSize: 11 }}>
                  <KeyboardReturn sx={{ fontSize: 12 }} />
                  ⌘S
                </Box>
              }
              placement="top"
            >
              <span>
                <Button
                  onClick={onSave}
                  disabled={isSaving}
                  size="small"
                  startIcon={isSaving ? <CircularProgress size={12} color="inherit" /> : null}
                  sx={{
                    bgcolor: BLUE,
                    color: '#fff',
                    fontFamily: SANS,
                    fontWeight: 600,
                    fontSize: 12.5,
                    textTransform: 'none',
                    minHeight: 32,
                    px: 2,
                    borderRadius: 999,
                    letterSpacing: '0.01em',
                    boxShadow: `0 0 0 1px ${alpha('#fff', 0.06)}, 0 1px 0 ${alpha('#fff', 0.12)} inset`,
                    '&:hover': { bgcolor: '#0053a8' },
                    '&.Mui-disabled': { bgcolor: alpha(BLUE, 0.5), color: 'rgba(255,255,255,0.7)' },
                  }}
                >
                  {mode === 'error' ? 'Reintentar' : 'Guardar'}
                </Button>
              </span>
            </Tooltip>
          </>
        )}

        {mode === 'saved' && (
          <CheckCircleOutline sx={{ fontSize: 18, color: '#86EFAC' }} />
        )}
      </Box>
    </Box>
  );
};

const StatusDot: React.FC<{ mode: 'dirty' | 'saving' | 'saved' | 'error' }> = ({ mode }) => {
  const color =
    mode === 'error' ? '#F87171' : mode === 'saved' ? '#4ADE80' : mode === 'saving' ? '#FDE68A' : BLUE;
  return (
    <Box sx={{ position: 'relative', display: 'grid', placeItems: 'center', width: 16, height: 16, flexShrink: 0 }}>
      {mode === 'error' ? (
        <ErrorOutline sx={{ fontSize: 16, color }} />
      ) : (
        <>
          <Box
            sx={{
              position: 'absolute',
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: color,
              opacity: 0.22,
              animation: mode !== 'saved' ? 'eu-ping 1.6s ease-out infinite' : 'none',
              '@keyframes eu-ping': {
                '0%': { transform: 'scale(0.6)', opacity: 0.45 },
                '80%, 100%': { transform: 'scale(1.6)', opacity: 0 },
              },
            }}
          />
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
        </>
      )}
    </Box>
  );
};
